import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { OutreachCareerTier } from 'fym-shared/src/music24/outreach/types';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useOutreachProfile } from '../hooks/useOutreachProfile';
import { QuestionPanel } from './QuestionPanel';
import { whoMessages as messages } from './whoMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for step 2; each part uses its own slice of it */
import styles from './Who.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The playlist sizes of a career tier. A copy of music24-api `TIER_FOLLOWER_RANGE` (targetFacts.ts). */
const TIER_FOLLOWERS: Record<OutreachCareerTier, { min: number; max: number | null }> = {
  emerging: { min: 50, max: 20_000 },
  developing: { min: 200, max: 100_000 },
  mid: { min: 1_000, max: 500_000 },
  established: { min: 10_000, max: null },
};

const SHOWN_ARTISTS = 3;

/** "Fine-tune" (design outreach-v2 c1): what Plug reads from the sound profile. Changes go to Your sound. */
const FineTuneComponent: React.FC<{ accountId: number }> = ({ accountId }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { profile } = useOutreachProfile(accountId);

  const looks = useMemo(() => {
    const notSet = intl.formatMessage(messages.notSetYet);
    const artists = profile?.similarArtists ?? [];
    const markets = profile?.markets ?? [];
    const size = profile === null || profile === undefined ? null : TIER_FOLLOWERS[profile.careerTier];
    return [
      {
        key: 'like',
        label: messages.soundsLike,
        value:
          artists.length === 0
            ? notSet
            : artists
                .slice(0, SHOWN_ARTISTS)
                .map((artist) => artist.name)
                .join(', '),
        note:
          artists.length > SHOWN_ARTISTS
            ? intl.formatMessage(messages.soundsLikeMore, { count: artists.length - SHOWN_ARTISTS })
            : null,
      },
      {
        key: 'countries',
        label: messages.countriesFirst,
        value:
          markets.length === 0
            ? notSet
            : markets.map((code) => intl.formatDisplayName(code, { type: 'region' }) ?? code).join(', '),
        note: intl.formatMessage(messages.countriesThen),
      },
      {
        key: 'size',
        label: messages.playlistSize,
        value:
          size === null
            ? notSet
            : size.max === null
              ? intl.formatMessage(messages.playlistSizeFrom, { min: intl.formatNumber(size.min) })
              : intl.formatMessage(messages.playlistSizeValue, {
                  min: intl.formatNumber(size.min),
                  max: intl.formatNumber(size.max),
                }),
        note: intl.formatMessage(messages.playlistSizeNote),
      },
    ].map((look) => (
      <div key={look.key} className={styles.look}>
        <span className={styles.lookKey}>{intl.formatMessage(look.label)}</span>
        <span className={styles.lookValue}>
          {look.value}
          {look.note !== null && <small>{look.note}</small>}
        </span>
      </div>
    ));
  }, [intl, profile]);

  return (
    <QuestionPanel
      title={intl.formatMessage(messages.fineTuneTitle)}
      hint={intl.formatMessage(messages.fineTuneHint)}
      right={
        <SMLButton size="sm" outline omitCap onClick={() => router.push(SMLRoutes.outreachProfile)}>
          {intl.formatMessage(messages.yourSound)}
        </SMLButton>
      }
    >
      <div className={styles.looks}>{looks}</div>
    </QuestionPanel>
  );
};

export const FineTune = TypedMemo(FineTuneComponent);
